import React, { memo, useCallback, useEffect, useState } from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Activity } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { LoadingSpinner, SearchField } from "@mapmycustomers/ui";

import noActivities from "@app/assets/list/preview/no-activities.svg";
import ActivitySort from "@app/component/activity/ActivitySort/ActivitySort";
import NoData from "@app/component/preview/components/NoData";
import { AnalyticsService, useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useSizeObserver from "@app/util/hook/useSizeObserver";

import consts from "../../styles/_consts.module.scss";

import styles from "./Activities.module.scss";
import ActivityItem from "./ActivityItem";

interface Props {
  activities: Activity[];
  activitiesFetcher: (search: string, filter: ActivityStatusOption) => void;
  analyticIssuer?: AnalyticsService;
  filter: ActivityStatusOption;
  loading: boolean;
  onChange?: () => void;
  onChangeSortState: (sortState: SortModel) => void;
  onSearchChange: (search: string) => void;
  postponeActivity: (activity: Activity) => void;
  postponeActivityLoadingIds: Activity["id"][];
  search: string;
  sortState: SortModel;
  toggleCompleteActivity: (activity: Activity) => void;
  toggleCompleteActivityLoadingIds: Activity["id"][];
  updateActivityNote: (data: { activity: Activity; onSuccess: () => void }) => void;
}

const EditActivities: React.FC<Props> = ({
  activities,
  activitiesFetcher,
  analyticIssuer,
  filter = ActivityStatusOption.ALL,
  loading,
  onChange,
  onChangeSortState,
  onSearchChange,
  postponeActivity,
  postponeActivityLoadingIds,
  search,
  sortState,
  toggleCompleteActivity,
  toggleCompleteActivityLoadingIds,
  updateActivityNote,
}) => {
  const intl = useIntl();
  const [field, setField] = useState<IField>(sortState[0].field);
  const [order, setOrder] = useState<SortOrder>(sortState[0].order);

  const activitiesAnalytics = useExtendedAnalytics("Activities", analyticIssuer);
  const handleSearchChange = useCallback(
    (value: string) => {
      onSearchChange(value);
      activitiesAnalytics?.clicked(["Search Activities"], { search: value });
    },
    [activitiesAnalytics, onSearchChange]
  );

  // call activitiesFetcher with delay when search is changed:
  const requestFilterOnSearchChange = useDebouncedCallback([
    useCallback(() => activitiesFetcher?.(search, filter), [activitiesFetcher, filter, search]),
    500,
  ]);

  useEffect(() => {
    requestFilterOnSearchChange();
  }, [requestFilterOnSearchChange]);

  const [width, , containerRef] = useSizeObserver<HTMLDivElement>();
  const shouldDisplayActions = width >= parseInt(consts.breakpointOtherTabs, 10);

  const handleApplyOrder = useCallback(() => {
    onChangeSortState([{ field, order }]);
  }, [field, onChangeSortState, order]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.activitiesFilters}>
        <div className={styles.activitiesSearch}>
          <SearchField
            onChange={handleSearchChange}
            placeholder={intl.formatMessage({
              id: "recordPane.activities.search.placeholder",
              defaultMessage: "Type to search",
              description: "Placeholder in a search text field for activities in *Edit panes",
            })}
            size="large"
            value={search}
          />
        </div>
        <ActivitySort
          activityStatusOption={filter}
          field={field}
          onApply={handleApplyOrder}
          onChangeField={setField}
          onChangeOrder={setOrder}
          order={order}
        />
      </div>
      {loading ? (
        <LoadingSpinner className={styles.loader} />
      ) : activities.length > 0 ? (
        <div className={cn({ [styles.activitiesContainerFull]: shouldDisplayActions })}>
          {activities.map((activity) => (
            <ActivityItem
              activity={activity}
              className={styles.activityItem}
              key={activity.id}
              onChange={onChange}
              postponeActivity={postponeActivity}
              postponeActivityLoadingIds={postponeActivityLoadingIds}
              showActions={shouldDisplayActions}
              toggleCompleteActivity={toggleCompleteActivity}
              toggleCompleteActivityLoadingIds={toggleCompleteActivityLoadingIds}
              updateActivityNote={updateActivityNote}
            />
          ))}
        </div>
      ) : (
        <NoData
          imageSrc={noActivities}
          text={intl.formatMessage({
            id: "recordPane.activitiesList.noData",
            defaultMessage: "Add an Activity to populate this list",
            description: "Text of the 'No Data' state in Activities section in a Edit pane",
          })}
        />
      )}
    </div>
  );
};

export default memo(EditActivities);
